import { useEffect, useState } from 'react';
import type { DeployStatus, DeployWatchLease } from '@volley/crucible-ipc-types/ipc-channels';
import { useCrucibleClient } from '../transport/context';

/**
 * Issue #637 — renderer hook for the ambient deploy-status surface.
 *
 * Subscribes to the `deploy:status` PUSH channel for the active gameId
 * and returns the latest `DeployStatus` snapshot (or `undefined` until
 * the first push arrives).
 *
 * Mirrors `usePhaseProgress`'s push-subscription shape: clear on gameId
 * change, filter cross-game pushes defensively, unsubscribe on cleanup.
 *
 * Lifecycle ownership (issue #637, Bugbot round 5) — this hook is the
 * OWNER of the main-side watcher's lifecycle. Owning hook instances
 * retain one renderer-shared lease per game: the first mount invokes
 * `deploy:watch:start` and the last cleanup invokes `deploy:watch:stop`
 * (Workspace unmount / route-leave / a gameId change / the Dashboard
 * status-panel collapse that nulls the gameId). Both are hook-driven,
 * so the watcher lives exactly as long as a surface is mounted for that
 * game — reliable regardless of `game:status` React-Query caching. The
 * old behaviour started the watcher as a side-effect of the
 * `game:status` query, which left the lifecycle asymmetric: after the
 * cleanup stop, a remount hitting a cached `game:status` (no new IPC)
 * never restarted polling. Driving start from mount closes that.
 *
 * Both IPCs are idempotent + flag-gated main-side: start re-claims an
 * already-active watch as a no-op; a start/stop when the flag is OFF
 * (or for a game that isn't watched) does nothing. Flag-OFF the watcher
 * never starts, nothing is ever pushed, and this hook stays `undefined`
 * forever — the consuming card renders nothing.
 *
 * `subscribeOnly` (crucible#3502 — Bugbot HIGH on #3502's own PR): a
 * SECOND consumer of this hook for the SAME gameId — `ArtAssetInspector`,
 * nested inside `Workspace` (`ArtWorkspace.tsx` mounts directly inside
 * `Workspace.tsx`'s JSX; `Workspace` itself already calls this hook
 * start/stop-owning for the same gameId) — would otherwise fight
 * Workspace's own watcher: deselecting an asset (or leaving the Art tab)
 * unmounts the inspector, which called `deploy:watch:stop` even though
 * Workspace is STILL mounted and still wants the watcher running, freezing
 * Workspace's own deploy card mid-pipeline. `subscribeOnly: true` still
 * subscribes to `deploy:status` and returns the latest snapshot, but never
 * calls `deploy:watch:start`/`stop` at all — it relies entirely on
 * whichever OTHER mounted consumer (always Workspace, per the nesting
 * invariant above) owns the watcher lifecycle for that gameId.
 *
 * Late-mount seeding (crucible#3502 round 2 — Bugbot MEDIUM): the main
 * watcher only pushes on a state TRANSITION (`maybePush`'s dedupe in
 * `deploy-watch.ts`), so a `subscribeOnly` mount that happens AFTER the
 * owner's watcher has already settled into a resting snapshot (e.g.
 * `tv-ready`) never gets a push at all — the card stays empty until the
 * NEXT transition, which for a resting state may never come. This hits
 * exactly the case the card exists for: reselecting an asset, or leaving
 * and returning to the Art tab, remounts `ArtAssetInspector` while
 * `Workspace` (the owner) is still mounted and already has a snapshot.
 *
 * Fix: a renderer-module-level cache (`lastKnownStatus`, keyed by
 * gameId), written by EVERY subscribed instance's push handler — owner
 * and `subscribeOnly` alike. `deploy:status` reaches the renderer via
 * `mainWindow.webContents.send`, a broadcast to the whole window, so
 * every `client.subscribe('deploy:status', …)` callback in this renderer
 * receives the same push regardless of which instance owns the watcher.
 * A mount seeds its initial state from the cache for the CURRENT gameId
 * before subscribing, so a late (re)mount shows the last-known snapshot
 * immediately — no new IPC, no main-process change. The cache is
 * intentionally never cleared on unmount: it's per-gameId, tiny, and
 * more than one owning consumer can exist for the same gameId at once
 * (Dashboard's status panel and Workspace can both hold an owning
 * subscription for the same game), so clearing it on any single owner's
 * teardown would race against another still-mounted one.
 */
const lastKnownStatus = new Map<string, DeployStatus>();

interface SharedDeployWatch {
  owners: Map<symbol, { invalidationVersion: number; sequence: number }>;
  lease: DeployWatchLease | null;
  starting: boolean;
  invalidationVersion: number;
  start: () => Promise<DeployWatchLease | null>;
  stop: (lease: DeployWatchLease) => void;
}

/**
 * Owning hook instances in one renderer share a single main-process lease per
 * game. Dashboard and Workspace can be mounted for the same game at once; a
 * per-effect start/stop pair would let the later owner's cleanup stop the
 * replacement generation while the earlier surface was still mounted.
 */
const sharedDeployWatches = new Map<string, SharedDeployWatch>();
let desiredDeployWatch: SharedDeployWatch | null = null;
let nextDeployWatchOwnerSequence = 0;
let survivorReelectionScheduled = false;
let survivorReelectionScheduleVersion = 0;

function isSupersededDeployWatchClear(status: DeployStatus): boolean {
  return (
    status.state === 'idle' &&
    status.error === undefined &&
    status.stages.every((stage) => stage.status === 'pending')
  );
}

function startSharedDeployWatch(gameId: string, shared: SharedDeployWatch): void {
  desiredDeployWatch = shared;
  shared.starting = true;
  const ownersAtStart = new Set(shared.owners.keys());
  const invalidationAtStart = shared.invalidationVersion;
  const retryForJoinedOwner = () => {
    shared.starting = false;
    if (
      [...shared.owners].some(
        ([owner, metadata]) =>
          !ownersAtStart.has(owner) && metadata.invalidationVersion === shared.invalidationVersion,
      ) &&
      sharedDeployWatches.get(gameId) === shared
    ) {
      startSharedDeployWatch(gameId, shared);
    }
  };
  void shared
    .start()
    .then((lease) => {
      if (!lease) {
        retryForJoinedOwner();
        return;
      }
      if (shared.invalidationVersion !== invalidationAtStart) {
        shared.stop(lease);
        retryForJoinedOwner();
        return;
      }
      shared.starting = false;
      if (shared.owners.size === 0 || sharedDeployWatches.get(gameId) !== shared) {
        shared.stop(lease);
        return;
      }
      shared.lease = lease;
    })
    .catch(() => {
      retryForJoinedOwner();
      // Best-effort — a start failure just means no live status; a later
      // owner can retry without losing the owners already mounted.
    })
    .finally(() => {
      if (desiredDeployWatch === null) startNewestSurvivingDeployWatch();
    });
}

function startNewestSurvivingDeployWatch(): void {
  let newest: { gameId: string; shared: SharedDeployWatch; ownerSequence: number } | undefined;
  for (const [gameId, shared] of sharedDeployWatches) {
    for (const owner of shared.owners.values()) {
      if (!newest || owner.sequence > newest.ownerSequence) {
        newest = { gameId, shared, ownerSequence: owner.sequence };
      }
    }
  }
  if (newest && !newest.shared.lease && !newest.shared.starting) {
    startSharedDeployWatch(newest.gameId, newest.shared);
  }
}

function scheduleSurvivorReelection(): void {
  if (desiredDeployWatch !== null || survivorReelectionScheduled) return;
  // Every mounted hook sees the same whole-window clear broadcast. Defer and
  // coalesce election until all callbacks have invalidated the old lease, or a
  // later callback could invalidate the replacement started by an earlier one.
  survivorReelectionScheduled = true;
  const scheduleVersion = ++survivorReelectionScheduleVersion;
  queueMicrotask(() => {
    if (scheduleVersion !== survivorReelectionScheduleVersion) return;
    survivorReelectionScheduled = false;
    if (desiredDeployWatch === null) startNewestSurvivingDeployWatch();
  });
}

function retainDeployWatch(
  gameId: string,
  start: () => Promise<DeployWatchLease | null>,
  stop: (lease: DeployWatchLease) => void,
): () => void {
  let shared = sharedDeployWatches.get(gameId);
  if (!shared) {
    shared = {
      owners: new Map(),
      lease: null,
      starting: false,
      invalidationVersion: 0,
      start,
      stop,
    };
    sharedDeployWatches.set(gameId, shared);
  }

  const owner = Symbol(gameId);
  shared.owners.set(owner, {
    invalidationVersion: shared.invalidationVersion,
    sequence: ++nextDeployWatchOwnerSequence,
  });
  shared.start = start;
  shared.stop = stop;
  if (!shared.lease && !shared.starting) startSharedDeployWatch(gameId, shared);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    shared.owners.delete(owner);
    if (shared.owners.size > 0) return;
    if (sharedDeployWatches.get(gameId) === shared) sharedDeployWatches.delete(gameId);
    if (shared.lease) shared.stop(shared.lease);
    if (desiredDeployWatch === shared) {
      desiredDeployWatch = null;
      startNewestSurvivingDeployWatch();
    }
  };
}

/**
 * Test-only — clears the shared cache between test cases so a push in
 * one test doesn't leak into a later test's fresh mount of the same
 * gameId. Not exported via any production surface.
 */
export function __resetDeployStatusCacheForTests(): void {
  lastKnownStatus.clear();
  sharedDeployWatches.clear();
  desiredDeployWatch = null;
  nextDeployWatchOwnerSequence = 0;
  survivorReelectionScheduled = false;
  survivorReelectionScheduleVersion += 1;
}

export function useDeployStatus(
  gameId: string | undefined,
  options: { enabled?: boolean; subscribeOnly?: boolean } = {},
): DeployStatus | undefined {
  const client = useCrucibleClient();
  const enabled = options.enabled ?? true;
  const subscribeOnly = options.subscribeOnly ?? false;
  // Lazy-init from the cache so a (re)mount's FIRST paint already shows the
  // last-known snapshot — seeding only in the effect below leaves one empty
  // frame (pill flicker on asset reselect / Art-tab return). The effect still
  // re-seeds on gameId/enabled changes; this covers mount only.
  const [status, setStatus] = useState<DeployStatus | undefined>(() =>
    gameId && (options.enabled ?? true) ? lastKnownStatus.get(gameId) : undefined,
  );

  useEffect(() => {
    if (!gameId || !enabled) {
      setStatus(undefined);
      return;
    }
    // Seed from the last-known snapshot for THIS game, if any, instead of
    // unconditionally blanking. A route change to a game we've never seen
    // a push for still clears to undefined (the cache has no entry); a
    // (re)mount for a game whose watcher already has a snapshot shows it
    // immediately rather than waiting for the next transition.
    setStatus(lastKnownStatus.get(gameId));

    let cancelled = false;
    const unsub = client.subscribe('deploy:status', (payload) => {
      // The module cache records EVERY broadcast, before any per-instance
      // guard — pushes for other games (incl. supersession idle clears)
      // and late teardown pushes must still update it, or a later mount
      // seeds a stale snapshot (Bugbot bc1616bb). Idempotent across the
      // many instances that all see the same whole-window broadcast.
      lastKnownStatus.set(payload.gameId, payload);
      if (isSupersededDeployWatchClear(payload) || payload.watchStopped) {
        const superseded = sharedDeployWatches.get(payload.gameId);
        if (superseded) {
          superseded.invalidationVersion += 1;
          superseded.lease = null;
          scheduleSurvivorReelection();
        }
      }
      if (cancelled) return;
      // Defensive cross-game filter: only one game is watched at a
      // time today, but a late push from a just-stopped watch could
      // still arrive after a route change.
      if (payload.gameId !== gameId) return;
      setStatus(payload);
    });

    const stopOwnedWatch = (lease: DeployWatchLease) => {
      void client.invoke('deploy:watch:stop', lease).catch(() => {
        // Best-effort lifecycle hygiene — main tears down on quit anyway.
      });
    };

    let releaseDeployWatch: (() => void) | undefined;
    if (!subscribeOnly) {
      // Same-game owning surfaces share one lease. The last owner releases
      // it; a release while start is still awaiting stops that exact lease
      // when it resolves, so rapid remount cannot tear down its replacement.
      releaseDeployWatch = retainDeployWatch(
        gameId,
        () => client.invoke('deploy:watch:start', { gameId }),
        stopOwnedWatch,
      );
    }

    return () => {
      cancelled = true;
      unsub();
      releaseDeployWatch?.();
    };
  }, [client, enabled, gameId, subscribeOnly]);

  return status;
}
